import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { overrideAssignmentSchema } from "@io-spoke/contracts/assignments";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Badge } from "../ui/badge";
import { formatTitleCase } from "../../lib/formatting";
import {
  overrideOwnerRequiredMessage,
  overrideRationaleRequiredMessage,
} from "../../lib/overrideValidationMessages";
import type { EntitySummary } from "../../lib/auth";
import type { AssigneeType } from "@io-spoke/contracts/assignments";
import {
  formatSlaBreachLabel,
  getSlaBadgeClassName,
  getSlaStateFromEntity,
} from "../../lib/slaState";

type ReviewQueueUpdatePayload = {
  title?: string;
  description?: string;
  systems?: string[];
  customerImpact?: string;
  priority?: string;
};

const overrideFormSchema = overrideAssignmentSchema.omit({ isFallback: true }).extend({
  assigneeId: z.string().trim().min(1, overrideOwnerRequiredMessage),
  rationale: z.string().trim().min(1, overrideRationaleRequiredMessage),
});
type OverrideFormValues = z.infer<typeof overrideFormSchema>;

type ReviewQueueCardProps = {
  cardId: string;
  item: EntitySummary;
  compactView: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
  saveState?: { status: "saving" | "saved" | "error"; message?: string };
  onRetry: () => void | Promise<void>;
  bulkError?: string;
  bulkHighlight?: boolean;
  onUpdate: (updates: ReviewQueueUpdatePayload) => Promise<void>;
  onAssign: (assigneeType: AssigneeType, assigneeId: string) => Promise<void>;
  onOverride: (assigneeType: AssigneeType, assigneeId: string, rationale: string) => Promise<void>;
  onApprove: () => Promise<void>;
  onViewDetail: () => void;
};

export function ReviewQueueCard({
  cardId,
  item,
  compactView,
  isExpanded,
  onToggleExpand,
  selected,
  onSelectChange,
  saveState,
  onRetry,
  bulkError,
  bulkHighlight,
  onUpdate,
  onAssign,
  onOverride,
  onApprove,
  onViewDetail,
}: ReviewQueueCardProps) {
  const approveAction = useAsyncAction();
  const overrideAction = useAsyncAction();
  const retryAction = useAsyncAction();
  const [actionError, setActionError] = useState<string | null>(null);
  const runAction = async (action: () => void | Promise<void>) => {
    setActionError(null);
    try { await action(); }
    catch (cause) { setActionError(cause instanceof Error ? cause.message : "Unable to complete this action. Try again."); }
  };
  const showDetails = !compactView || isExpanded;
  let queueLabel: string | null = null;
  if (item.assignment?.isFallback) queueLabel = "Fallback queue";
  else if (item.assignment?.assigneeType === "team") queueLabel = `${formatTitleCase(item.assignment.assigneeId)} queue`;
  const overrideForm = useForm<OverrideFormValues>({
    resolver: zodResolver(overrideFormSchema),
    defaultValues: {
      assigneeType: item.assignment?.assigneeType ?? "team",
      assigneeId: item.assignment?.assigneeId ?? "",
      rationale: "",
    },
  });
  const overrideFormId = `review-override-${item.id}`;
  const slaState = getSlaStateFromEntity({
    targetAt: item.slaTargetAt,
    breachedAt: item.slaBreachedAt,
  });
  const slaBadgeLabel =
    slaState.status === "breached"
      ? "SLA breached"
      : slaState.status === "at_risk"
        ? formatSlaBreachLabel(slaState.minutesRemaining) ?? "SLA at risk"
        : null;

  useEffect(() => {
    overrideForm.reset({
      assigneeType: item.assignment?.assigneeType ?? "team",
      assigneeId: item.assignment?.assigneeId ?? "",
      rationale: "",
    });
  }, [item.assignment?.assigneeId, item.assignment?.assigneeType, item.id, overrideForm]);

  return (
    <Card
      id={cardId}
      tabIndex={-1}
      className={
        bulkHighlight
          ? "border-amber-500/70 bg-amber-50 dark:bg-amber-950/20"
          : "border-border bg-card/60"
      }
    >
      <CardHeader className="space-y-1">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-lg">{item.title}</CardTitle>
          {compactView ? (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={onToggleExpand}
            >
              {isExpanded ? "Hide details" : "Edit details"}
            </Button>
          ) : null}
        </div>
        <CardDescription>
          {item.type} - {item.priority} priority
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          {queueLabel ? (
            <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="text-foreground/90">{queueLabel}</Badge>
            </div>
          ) : null}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Checkbox
              id={`bulk-select-${item.id}`}
              checked={selected}
              onCheckedChange={(checked) => onSelectChange(checked === true)}
              aria-label={`Select ${item.title}`}
            />
            <Label htmlFor={`bulk-select-${item.id}`} className="flex min-h-11 cursor-pointer items-center text-xs text-muted-foreground">
              Select for bulk actions
            </Label>
          </div>
        </div>
        {saveState ? (
          <div
            className={
              saveState.status === "error"
                ? "rounded-md border border-red-500/40 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200"
                : "rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
            }
            role={saveState.status === "error" ? "alert" : "status"}
          >
            {saveState.message ??
              (saveState.status === "saving" ? "Saving..." : "Saved.")}
            {saveState.status === "error" || retryAction.pending ? (
              <Button
                variant="ghost"
                className="ml-2 h-6 px-2 text-xs"
                onClick={() => void retryAction.run(() => runAction(onRetry))}
                loading={retryAction.pending}
                loadingLabel="Retrying…"
              >
                Retry
              </Button>
            ) : null}
          </div>
        ) : null}
        {item.slaTargetAt ? (
          <div
            className={
              slaState.status === "breached"
                ? "rounded-md border border-red-500/40 bg-red-50 px-3 py-2 text-xs text-red-900 dark:bg-red-950/20 dark:text-red-100"
                : slaState.status === "at_risk"
                  ? "rounded-md border border-amber-500/40 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/20 dark:text-amber-100"
                  : "rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground"
            }
          >
            <div className="flex flex-wrap items-center gap-2">
              {slaBadgeLabel ? (
                <Badge
                  variant="outline"
                  className={`${getSlaBadgeClassName(slaState.status)}`}
                >
                  {slaBadgeLabel}
                </Badge>
              ) : null}
              {item.slaRule?.name ? (
                <span>{item.slaRule.name}</span>
              ) : null}
            </div>
            <div className="mt-2 space-y-1">
              <p>SLA target: {new Date(item.slaTargetAt).toLocaleString()}</p>
              {item.slaBreachedAt ? (
                <p>Breached: {new Date(item.slaBreachedAt).toLocaleString()}</p>
              ) : null}
            </div>
          </div>
        ) : null}
        {item.approvalChainProgress ? (
          <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {item.approvalChainProgress.status === "completed" ? (
              "Approval chain complete"
            ) : (
              <>
                {item.approvalChainProgress.status === "pending"
                  ? "Approval pending"
                  : "Approval in progress"}
                {" • "}
                Next:{" "}
                {item.approvalChainProgress.currentStep?.label ??
                  item.approvalChainProgress.steps[0]?.label ??
                  "TBD"}
                {" ("}
                {item.approvalChainProgress.completedSteps}/
                {item.approvalChainProgress.totalSteps}
                {")"}
              </>
            )}
          </div>
        ) : null}
        {showDetails ? (
          <>
            <div className="space-y-2">
              <Label htmlFor={`title-${item.id}`}>Title</Label>
              <Input
                id={`title-${item.id}`}
                type="text"
                defaultValue={item.title}
                onBlur={async (event) => {
                  await onUpdate({ title: event.target.value });
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`description-${item.id}`}>Description</Label>
              <Textarea
                id={`description-${item.id}`}
                defaultValue={item.description}
                onBlur={async (event) => {
                  await onUpdate({ description: event.target.value });
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`systems-${item.id}`}>Impacted systems</Label>
              <Input
                id={`systems-${item.id}`}
                type="text"
                defaultValue={item.systems.join(", ")}
                placeholder="auth, billing"
                onBlur={async (event) => {
                  const systems = event.target.value
                    .split(",")
                    .map((value) => value.trim())
                    .filter((value) => value.length > 0);
                  await onUpdate({ systems });
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`impact-${item.id}`}>Customer impact</Label>
              <Input
                id={`impact-${item.id}`}
                type="text"
                defaultValue={item.customerImpact ?? ""}
                placeholder="multiple customers affected"
                onBlur={async (event) => {
                  await onUpdate({ customerImpact: event.target.value });
                }}
              />
            </div>
          </>
        ) : null}
        <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Confidence: {item.confidence ?? "N/A"}
        </div>
        {item.suggestedOwner ? (
          <div className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Suggested owner: {item.suggestedOwner.type} {item.suggestedOwner.id}
          </div>
        ) : null}
        {bulkError ? (
          <p className="text-xs text-red-700 dark:text-red-400" role="alert">
            {bulkError}
          </p>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor={`priority-${item.id}`}>Priority</Label>
          <Select
            defaultValue={item.priority}
            onValueChange={async (value) => {
              await onUpdate({ priority: value });
            }}
          >
            <SelectTrigger id={`priority-${item.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">low</SelectItem>
              <SelectItem value="medium">medium</SelectItem>
              <SelectItem value="high">high</SelectItem>
              <SelectItem value="critical">critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <form
          id={overrideFormId}
          noValidate
          onSubmit={overrideForm.handleSubmit((values) => overrideAction.run(() => runAction(() => onOverride(values.assigneeType, values.assigneeId, values.rationale))))}
          className={showDetails ? "space-y-4" : undefined}
        >
          {showDetails ? (
            <>
            <div className="space-y-2">
              <Label htmlFor={`assignee-type-${item.id}`}>Owner type</Label>
              <Controller
                name="assigneeType"
                control={overrideForm.control}
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={async (value) => {
                      const nextType = value as AssigneeType;
                      field.onChange(nextType);
                      const trimmed = overrideForm.getValues("assigneeId").trim();
                      if (trimmed) {
                        await onAssign(nextType, trimmed);
                      }
                    }}
                  >
                    <SelectTrigger id={`assignee-type-${item.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="team">team</SelectItem>
                      <SelectItem value="role">role</SelectItem>
                      <SelectItem value="agent">agent</SelectItem>
                      <SelectItem value="human">human</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`assignee-id-${item.id}`}>Owner</Label>
              <Input
                id={`assignee-id-${item.id}`}
                type="text"
                placeholder="support"
                {...overrideForm.register("assigneeId", {
                  onBlur: async (event) => {
                    if (item.assignment) {
                      return;
                    }
                    const trimmed = event.target.value.trim();
                    if (trimmed) {
                      await onAssign(overrideForm.getValues("assigneeType"), trimmed);
                    }
                  },
                })}
                aria-invalid={Boolean(overrideForm.formState.errors.assigneeId)}
                aria-describedby={overrideForm.formState.errors.assigneeId ? `assignee-id-${item.id}-error` : undefined}
              />
              {overrideForm.formState.errors.assigneeId ? (
                <p id={`assignee-id-${item.id}-error`} className="text-xs text-red-700 dark:text-red-400" role="alert">
                  {overrideForm.formState.errors.assigneeId.message}
                </p>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor={`assignee-rationale-${item.id}`}>
                Override rationale
              </Label>
              <Textarea
                id={`assignee-rationale-${item.id}`}
                placeholder="Routing to platform team due to workload."
                {...overrideForm.register("rationale")}
                aria-invalid={Boolean(overrideForm.formState.errors.rationale)}
                aria-describedby={overrideForm.formState.errors.rationale ? `assignee-rationale-${item.id}-error` : undefined}
              />
              {overrideForm.formState.errors.rationale ? (
                <p id={`assignee-rationale-${item.id}-error`} className="text-xs text-red-700 dark:text-red-400" role="alert">
                  {overrideForm.formState.errors.rationale.message}
                </p>
              ) : null}
            </div>
            </>
          ) : null}
        </form>
      </CardContent>
      {actionError ? <p className="px-6 pb-3 text-sm text-destructive" role="alert">{actionError}</p> : null}
      <CardFooter className="flex flex-wrap items-center justify-between gap-2">
        <Badge
          variant="outline"
          className="text-foreground/90"
        >
          {item.status ? formatTitleCase(item.status) : "Status"}
        </Badge>
        <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
          {showDetails ? (
            <Button
              type="submit"
              form={overrideFormId}
              variant="outline"
              className="w-full sm:w-auto"
              loading={overrideAction.pending} loadingLabel="Saving override…" disabled={approveAction.pending}
            >
              Override
            </Button>
          ) : null}
          <Button className="w-full sm:w-auto" loading={approveAction.pending} loadingLabel="Approving…" disabled={overrideAction.pending} onClick={() => void approveAction.run(() => runAction(onApprove))}>
            Approve
          </Button>
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={onViewDetail}
          >
            View detail
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
